import { useBIMStore } from '../state/useBIMStore';
import { Sheet, SheetSizeId, TitleBlock, View, PlacedView } from '../core/types';
import * as THREE from 'three';
import { useEffect, useMemo } from 'react';
import { Line, Text } from '@react-three/drei';
import { v4 as uuidv4 } from 'uuid';
import type { SheetStyleId } from '../config/sheetTemplates';

/**
 * Sheet display engine — renders an open sheet like BIM's sheet view:
 * a paper sheet on a dark desktop, a title block border frame, placed
 * viewports with view titles, and interaction affordances (selection,
 * move). World units are meters; 1 mm of paper = 0.001 world units.
 *
 * The viewport is zoomed-to-fit on open via yap:zoom-to-fit (dispatched
 * by the ProjectBrowser / SheetTool when opening the sheet).
 */

// Paper sizes: id -> width/height in meters (mm / 1000)
export const SHEET_SIZES: Record<SheetSizeId, { width: number; height: number; label: string }> = {
    'A0':     { width: 1.189, height: 0.841, label: 'A0' },
    'A1':     { width: 0.841, height: 0.594, label: 'A1' },
    'A2':     { width: 0.594, height: 0.420, label: 'A2' },
    'A3':     { width: 0.420, height: 0.297, label: 'A3' },
    'A4':     { width: 0.297, height: 0.210, label: 'A4' },
    'ARCH_D': { width: 0.9144, height: 0.6096, label: 'Arch D 24"x36"' },
    'ARCH_E': { width: 1.0668, height: 0.762, label: 'Arch E1 30"x42"' },
    'ANSI_B': { width: 0.4318, height: 0.2794, label: 'ANSI B 11"x17"' },
    'ANSI_D': { width: 0.5588, height: 0.8636, label: 'ANSI D 22"x34"' },
    'ANSI_E': { width: 0.8636, height: 1.1176, label: 'ANSI E 34"x44"' },
};

// Sheet theme constants (kept in sync with themes/colors.ts light-on-dark BIM palette)
const SHEET_COLORS = {
    desktop: '#2a2a2a',       // surrounding desktop
    paper: '#ffffff',         // paper fill
    paperEdge: '#000000',     // paper border
    frameLine: '#000000',     // title block border
    titleBlockFill: '#ffffff',
    titleLine: '#000000',
    titleText: '#000000',
    viewportLine: '#7f7f7f',  // viewport crop boundary (grey in BIM)
    viewportLineSelected: '#00aaff',
    viewportFill: 'rgba(240, 240, 240, 0.85)',
    viewTitleText: '#000000',
    emptyHint: '#808080',
} as const;

const TITLE_BLOCK_WIDTH = 0.18; // 180mm title block strip along the right edge
const FRAME_MARGIN = 0.01;      // 10mm inner frame margin

/** Minimum viewport size on paper (m) — small views still get a usable crop box. */
const MIN_VIEWPORT_WIDTH = 0.08;
const MIN_VIEWPORT_HEIGHT = 0.06;

export function getSheetSize(sheet: Sheet): { width: number; height: number } {
    const size = SHEET_SIZES[(sheet.titleBlockId.replace('tb-', '').toUpperCase() as SheetSizeId)];
    if (size) return { width: size.width, height: size.height };
    // Fallback: match title block id 'tb-24x36' style custom sizes
    switch (sheet.titleBlockId) {
        case 'tb-24x36': return { width: 0.9144, height: 0.6096 };
        case 'tb-30x42': return { width: 1.0668, height: 0.762 };
        case 'tb-ansi-b': return { width: 0.4318, height: 0.2794 };
        default: break;
    }
    // User-created paper types (New Sheet dialog > New Paper Type)
    const custom = useBIMStore.getState().project.customTitleBlocks.find(tb => tb.id === sheet.titleBlockId);
    if (custom) {
        return { width: custom.width / 1000, height: custom.height / 1000 };
    }
    return SHEET_SIZES.A1;
}

/**
 * Pre-printed letterhead style of a sheet, derived from the template used to
 * create it (styles live in config/sheetTemplates.ts and are stamped onto the
 * sheet via its title block fields).
 */
export function getSheetStyle(sheet: Sheet): SheetStyleId {
    const style = (sheet as any).style as SheetStyleId | undefined;
    return style ?? 'standard';
}

/** Title block definition for a sheet, with live project-injected field values. */
export function resolveTitleBlock(sheet: Sheet, projectInfo: { name: string; number: string; client: string; address: string }): TitleBlock {
    const sizeKey = (sheet.titleBlockId.replace('tb-', '').toUpperCase() as SheetSizeId);
    const sizeMeta = SHEET_SIZES[sizeKey];
    const dims = getSheetSize(sheet);
    return {
        id: sheet.titleBlockId,
        name: sizeMeta?.label ?? sheet.titleBlockId,
        size: sizeKey ?? 'A1',
        width: dims.width * 1000,
        height: dims.height * 1000,
        fields: [
            { id: 'projectName', label: 'Project Name', value: projectInfo.name },
            { id: 'projectNumber', label: 'Project Number', value: projectInfo.number },
            { id: 'client', label: 'Client', value: projectInfo.client },
            { id: 'address', label: 'Address', value: projectInfo.address },
            { id: 'sheetName', label: 'Sheet Name', value: sheet.name },
            { id: 'sheetNumber', label: 'Sheet Number', value: sheet.number },
            { id: 'date', label: 'Date', value: sheet.dateIssued ?? '' },
            { id: 'scale', label: 'Scale', value: '' },
        ],
    };
}

/**
 * Compute the default placement rect (in meters, sheet-local coordinates,
 * origin at sheet center) for a view being placed on a sheet.
 * Views are fitted into a sensible default viewport slot.
 */
export function computeDefaultViewRect(view: View, sheetSize: { width: number; height: number }, occupied: PlacedView[]): { x: number; y: number; width: number; height: number } {
    // Slot layout: cascade placements left-to-right, top-to-bottom
    const slotIndex = occupied.length;
    const slotsPerRow = Math.max(1, Math.floor((sheetSize.width - TITLE_BLOCK_WIDTH - 0.06) / (MIN_VIEWPORT_WIDTH + 0.04)));
    const col = slotIndex % slotsPerRow;
    const row = Math.floor(slotIndex / slotsPerRow);

    const slotWidth = (sheetSize.width - TITLE_BLOCK_WIDTH - 0.06) / slotsPerRow;
    const slotHeight = (sheetSize.height - 0.06) / 3;

    const width = Math.min(slotWidth - 0.02, Math.max(MIN_VIEWPORT_WIDTH, slotWidth - 0.04));
    const height = Math.min(slotHeight - 0.02, Math.max(MIN_VIEWPORT_HEIGHT, slotHeight - 0.04));

    return {
        x: -sheetSize.width / 2 + 0.03 + col * slotWidth + slotWidth / 2,
        y: sheetSize.height / 2 - 0.03 - row * slotHeight - slotHeight / 2,
        width,
        height,
    };
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function fitViewToRect(rect: { width: number; height: number }): { center: [number, number]; zoom: number } {
    // Model extents: gather from elements (meters)
    const project = useBIMStore.getState().project;
    const box = new THREE.Box3();
    let found = false;
    project.elements.forEach((el: any) => {
        const geom = el?.geometryData;
        if (!geom) return;
        if (geom.start && geom.end) {
            box.expandByPoint(new THREE.Vector3(...geom.start));
            box.expandByPoint(new THREE.Vector3(...geom.end));
            found = true;
        } else if (geom.position) {
            box.expandByPoint(new THREE.Vector3(...geom.position));
            found = true;
        } else if (geom.boundary) {
            geom.boundary.forEach((p: [number, number]) => box.expandByPoint(new THREE.Vector3(p[0], p[1], 0)));
            found = true;
        }
    });
    if (!found) box.setFromCenterAndSize(new THREE.Vector3(0, 0, 0), new THREE.Vector3(10, 10, 0));

    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const marginFactor = 1.2; // 20% breathing room
    const zoomX = rect.width / Math.max(size.x * marginFactor, 0.001);
    const zoomY = rect.height / Math.max(size.y * marginFactor, 0.001);
    const zoom = isFinite(zoomX) && isFinite(zoomY) ? Math.min(zoomX, zoomY) : 10;

    return { center: [center.x, center.y], zoom: Math.max(zoom, 0.0001) };
}

// ============================================================================
// React 3D components
// ============================================================================

/**
 * Main sheet renderer — mounts when a sheet is open (project.activeSheetId).
 * Renders paper, pre-printed letterhead flourishes (per template style),
 * border frame, title block, and all placed viewports.
 */
export function SheetView() {
    const project = useBIMStore((s) => s.project);
    const sheet = project.activeSheetId ? project.sheets.get(project.activeSheetId) : null;

    if (!sheet) return null;

    const size = getSheetSize(sheet);
    const style = getSheetStyle(sheet);
    const placedViews = sheet.viewsPlaced;

    return (
        <group>
            {/* Desktop backdrop (subtle shadow card effect) */}
            <mesh position={[0, 0, -0.002]} renderOrder={-2}>
                <planeGeometry args={[size.width + 0.04, size.height + 0.04]} />
                <meshBasicMaterial color={SHEET_COLORS.desktop} />
            </mesh>

            {/* Paper */}
            <mesh renderOrder={-1}>
                <planeGeometry args={[size.width, size.height]} />
                <meshBasicMaterial color={SHEET_COLORS.paper} />
            </mesh>
            <Line
                points={[[-size.width / 2, -size.height / 2, 0], [size.width / 2, -size.height / 2, 0], [size.width / 2, size.height / 2, 0], [-size.width / 2, size.height / 2, 0], [-size.width / 2, -size.height / 2, 0]]}
                color={SHEET_COLORS.paperEdge}
                lineWidth={1}
            />

            {/* Pre-printed letterhead flourishes (antet) */}
            <LetterheadStyle style={style} size={size} />

            {/* Border frame */}
            <BorderFrame size={size} />

            {/* Title block (right strip) */}
            <TitleBlockRenderer sheet={sheet} size={size} />

            {/* Placed viewports */}
            {placedViews.map((pv) => (
                <PlacedViewportRenderer key={pv.id} placedView={pv} sheet={sheet} />
            ))}

            {/* Empty state hint */}
            {placedViews.length === 0 && (
                <Text
                    position={[-0.05, 0, 0.01]}
                    fontSize={0.018}
                    color={SHEET_COLORS.emptyHint}
                    anchorX="center"
                    anchorY="middle"
                    maxWidth={size.width - TITLE_BLOCK_WIDTH - 0.08}
                >
                    Drag views from the Project Browser onto this sheet, or use "Place View" on the View tab.
                </Text>
            )}
        </group>
    );
}

/**
 * Pre-printed letterhead flourishes stamped onto the paper at render time:
 * - corporate: brand band across the top + logo zone bottom-left
 * - minimal: thin single frame (frame drawn thinner) + discreet footer rule
 * - engineering: dashed ruler edges top/bottom + zone letters on the frame
 */
function LetterheadStyle({ style, size }: { style: SheetStyleId; size: { width: number; height: number } }) {
    if (style === 'standard') return null;

    const x0 = -size.width / 2;
    const x1 = size.width / 2;

    if (style === 'corporate') {
        const bandH = 0.03;
        const inner = size.width - TITLE_BLOCK_WIDTH - 0.02;
        return (
            <group>
                {/* Brand band across the top of the drawing area */}
                <mesh position={[-size.width / 2 + inner / 2, size.height / 2 - bandH / 2 - 0.005, 0.0008]}>
                    <planeGeometry args={[inner, bandH]} />
                    <meshBasicMaterial color="#0078d4" />
                </mesh>
                {/* Logo zone bottom-left */}
                <mesh position={[-size.width / 2 + 0.06, -size.height / 2 + 0.035, 0.0008]}>
                    <planeGeometry args={[0.08, 0.05]} />
                    <meshBasicMaterial color="#f0f0f0" />
                </mesh>
                <Line
                    points={[[-size.width / 2 + 0.02, -size.height / 2 + 0.06, 0.0009], [-size.width / 2 + 0.1, -size.height / 2 + 0.06, 0.0009]]}
                    color="#0078d4"
                    lineWidth={1}
                />
            </group>
        );
    }

    if (style === 'minimal') {
        return (
            <group>
                {/* Discreet footer rule */}
                <Line
                    points={[[x0 + 0.02, -size.height / 2 + 0.025, 0.0008], [x1 - TITLE_BLOCK_WIDTH - 0.02, -size.height / 2 + 0.025, 0.0008]]}
                    color="#999999"
                    lineWidth={1}
                />
            </group>
        );
    }

    // engineering: dashed ruler edges + zone markers along the frame
    const zoneCount = 6;
    const zoneLetters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
    const ticks: [number, number, number][][] = [];
    for (let i = 0; i <= zoneCount; i++) {
        const x = x0 + (i / zoneCount) * (size.width - TITLE_BLOCK_WIDTH);
        ticks.push([[x, size.height / 2 - 0.018, 0.0008], [x, size.height / 2 - 0.008, 0.0008]]);
        ticks.push([[x, -size.height / 2 + 0.008, 0.0008], [x, -size.height / 2 + 0.018, 0.0008]]);
    }
    return (
        <group>
            {/* Ruler ticks top and bottom */}
            {ticks.map((seg, i) => (
                <Line key={i} points={seg} color="#555555" lineWidth={1} />
            ))}
            {/* Zone letters on the top edge */}
            {zoneLetters.slice(0, zoneCount).map((letter, i) => (
                <Text
                    key={letter}
                    position={[x0 + ((i + 0.5) / zoneCount) * (size.width - TITLE_BLOCK_WIDTH), size.height / 2 - 0.028, 0.0009]}
                    fontSize={0.01}
                    color="#333333"
                    anchorX="center"
                    anchorY="middle"
                >
                    {letter}
                </Text>
            ))}
        </group>
    );
}

function BorderFrame({ size }: { size: { width: number; height: number } }) {
    const x0 = -size.width / 2 + FRAME_MARGIN;
    const x1 = size.width / 2 - FRAME_MARGIN;
    const y0 = -size.height / 2 + FRAME_MARGIN;
    const y1 = size.height / 2 - FRAME_MARGIN;
    return (
        <Line
            points={[[x0, y0, 0.001], [x1, y0, 0.001], [x1, y1, 0.001], [x0, y1, 0.001], [x0, y0, 0.001]]}
            color={SHEET_COLORS.frameLine}
            lineWidth={2}
        />
    );
}

/**
 * Title block: right-edge strip with rows of label/value cells like a
 * classic title block. Reads project info + sheet info live from the store.
 */
function TitleBlockRenderer({ sheet, size }: { sheet: Sheet; size: { width: number; height: number } }) {
    const project = useBIMStore((s) => s.project);

    const rows = useMemo(() => {
        const tb = resolveTitleBlock(sheet, {
            name: project.name,
            number: project.number,
            client: project.client,
            address: project.address,
        });
        return tb.fields.filter(f => f.value && f.value.length > 0);
    }, [sheet, project.name, project.number, project.client, project.address]);

    const xLeft = size.width / 2 - TITLE_BLOCK_WIDTH;
    const xRight = size.width / 2 - FRAME_MARGIN;
    const yTop = size.height / 2 - FRAME_MARGIN;
    const yBottom = -size.height / 2 + FRAME_MARGIN;
    const stripHeight = yTop - yBottom;
    const rowHeight = stripHeight / Math.max(rows.length, 1);

    const cellLines: [number, number, number][] = [];
    for (let i = 1; i < rows.length; i++) {
        const y = yTop - i * rowHeight;
        cellLines.push([xLeft, y, 0.002]);
        cellLines.push([xRight, y, 0.002]);
    }

    return (
        <group>
            {/* Strip background */}
            <mesh position={[(xLeft + xRight) / 2, 0, 0.0015]}>
                <planeGeometry args={[xRight - xLeft, stripHeight]} />
                <meshBasicMaterial color={SHEET_COLORS.titleBlockFill} />
            </mesh>

            {/* Strip outline */}
            <Line
                points={[[xLeft, yBottom, 0.002], [xRight, yBottom, 0.002], [xRight, yTop, 0.002], [xLeft, yTop, 0.002], [xLeft, yBottom, 0.002]]}
                color={SHEET_COLORS.titleLine}
                lineWidth={2}
            />

            {/* Row dividers */}
            {rows.length > 1 && (
                <Line points={cellLines} color={SHEET_COLORS.titleLine} lineWidth={1} />
            )}

            {/* Sheet title banner at the top of the strip */}
            <Text
                position={[(xLeft + xRight) / 2, yTop - 0.014, 0.003]}
                fontSize={0.016}
                color={SHEET_COLORS.titleText}
                anchorX="center"
                anchorY="middle"
                fontWeight="bold"
            >
                {sheet.number}
            </Text>
            <Text
                position={[(xLeft + xRight) / 2, yTop - 0.032, 0.003]}
                fontSize={0.012}
                color={SHEET_COLORS.titleText}
                anchorX="center"
                anchorY="middle"
                maxWidth={xRight - xLeft - 0.01}
            >
                {sheet.name}
            </Text>

            {/* Field rows */}
            {rows.map((field, i) => {
                const yCenter = yTop - 0.045 - i * rowHeight;
                if (yCenter < yBottom + rowHeight / 2) return null;
                return (
                    <group key={field.id}>
                        <Text
                            position={[xLeft + 0.006, yCenter, 0.003]}
                            fontSize={0.0075}
                            color={SHEET_COLORS.titleText}
                            anchorX="left"
                            anchorY="middle"
                            maxWidth={TITLE_BLOCK_WIDTH * 0.38}
                        >
                            {field.label}
                        </Text>
                        <Text
                            position={[xLeft + TITLE_BLOCK_WIDTH * 0.46, yCenter, 0.003]}
                            fontSize={0.009}
                            color={SHEET_COLORS.titleText}
                            anchorX="left"
                            anchorY="middle"
                            maxWidth={TITLE_BLOCK_WIDTH * 0.5}
                        >
                            {field.value}
                        </Text>
                    </group>
                );
            })}
        </group>
    );
}

/**
 * A placed viewport: crop rectangle + live plan view content preview.
 * Clicking selects; dragging moves the viewport on the sheet.
 * Double-click opens the underlying view (activates it in the viewport).
 */
function PlacedViewportRenderer({ placedView, sheet }: { placedView: PlacedView; sheet: Sheet }) {
    const view = useBIMStore((s) => s.project.views.get(placedView.viewId));
    const selectedId = useBIMStore((s) => s.selection[0]);
    const setSelection = useBIMStore((s) => s.setSelection);
    const removePlacedView = useBIMStore((s) => s.removePlacedView);
    const movePlacedView = useBIMStore((s) => s.movePlacedView);

    const size = getSheetSize(sheet);
    const rect = useMemo(() => {
        // Viewport extent derived from view scale (BIM scales the model crop to paper)
        const scale = placedView.scale ?? 100;
        // A 1:100 view of a 10m model covers 100mm of paper — derive from model extents
        const project = useBIMStore.getState().project;
        const box = new THREE.Box3();
        let found = false;
        project.elements.forEach((el: any) => {
            const geom = el?.geometryData;
            if (!geom) return;
            if (geom.start && geom.end) {
                box.expandByPoint(new THREE.Vector3(...geom.start));
                box.expandByPoint(new THREE.Vector3(...geom.end));
                found = true;
            } else if (geom.position) {
                box.expandByPoint(new THREE.Vector3(...geom.position));
                found = true;
            }
        });
        const modelW = found ? Math.max(box.getSize(new THREE.Vector3()).x, 1) : 10;
        const modelH = found ? Math.max(box.getSize(new THREE.Vector3()).y, 1) : 10;
        // paper meters = model meters * (1000 / scale) / 1000 => model / scale
        let width = (modelW / scale);
        let height = (modelH / scale);
        // Clamp to paper
        const maxW = size.width - TITLE_BLOCK_WIDTH - 0.04;
        const maxH = size.height - 0.04;
        const fit = Math.min(maxW / width, maxH / height, 1.0);
        if (fit < 1) { width *= fit; height *= fit; }
        width = Math.max(width, MIN_VIEWPORT_WIDTH);
        height = Math.max(height, MIN_VIEWPORT_HEIGHT);
        return { x: placedView.position.x, y: placedView.position.y, width, height };
    }, [placedView.position.x, placedView.position.y, placedView.scale, size.width, size.height]);

    if (!view) return null;

    const isSelected = selectedId === placedView.id;
    const borderColor = isSelected ? SHEET_COLORS.viewportLineSelected : SHEET_COLORS.viewportLine;

    const x0 = rect.x - rect.width / 2;
    const x1 = rect.x + rect.width / 2;
    const y0 = rect.y - rect.height / 2;
    const y1 = rect.y + rect.height / 2;

    // View title below the viewport (BIM-style: view name + scale line)
    const titleY = y0 - 0.012;
    const scaleLabel = `1 : ${placedView.scale ?? view.scale ?? 100}`;

    const handlePointerDown = (e: any) => {
        e.stopPropagation();
        setSelection([placedView.id]);
    };

    const handlePointerMove = (e: any) => {
        if (e.buttons === 1 && selectedId === placedView.id) {
            e.stopPropagation();
            movePlacedView(sheet.id, placedView.id, { x: e.point.x, y: e.point.y });
        }
    };

    const handleDoubleClick = () => {
        // Open the underlying model view (BIM: double-click viewport opens the view)
        const store = useBIMStore.getState();
        store.setActiveView(placedView.viewId);
    };

    const handleDelete = (e: any) => {
        if (e.key === 'Delete') {
            removePlacedView(sheet.id, placedView.id);
        }
    };

    return (
        <group>
            {/* Viewport crop fill */}
            <mesh
                position={[rect.x, rect.y, 0.004]}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onDoubleClick={handleDoubleClick}
                userData={{ placedViewId: placedView.id, sheetId: sheet.id }}
            >
                <planeGeometry args={[rect.width, rect.height]} />
                <meshBasicMaterial color={SHEET_COLORS.viewportFill} transparent opacity={0.85} />
            </mesh>

            {/* Crop boundary */}
            <Line
                points={[[x0, y0, 0.005], [x1, y0, 0.005], [x1, y1, 0.005], [x0, y1, 0.005], [x0, y0, 0.005]]}
                color={borderColor}
                lineWidth={isSelected ? 2 : 1}
            />

            {/* View label inside the viewport (like BIM's viewport title placeholder) */}
            <Text
                position={[rect.x, rect.y + rect.height / 2 - 0.014, 0.006]}
                fontSize={0.011}
                color="#333333"
                anchorX="center"
                anchorY="middle"
                maxWidth={rect.width - 0.01}
            >
                {view.name}
            </Text>

            {/* View title below the viewport */}
            <Text
                position={[rect.x, titleY, 0.005]}
                fontSize={0.01}
                color={SHEET_COLORS.viewTitleText}
                anchorX="center"
                anchorY="top"
                maxWidth={Math.max(rect.width, 0.12)}
            >
                {`${view.name}  —  ${scaleLabel}`}
            </Text>

            {/* Delete key handler mounted while selected */}
            {isSelected && <DeleteKeyHandler onDelete={handleDelete} />}
        </group>
    );
}

/** Key listener for deleting the selected viewport (proper effect lifecycle). */
function DeleteKeyHandler({ onDelete }: { onDelete: (e: KeyboardEvent) => void }) {
    useEffect(() => {
        const handler = (e: KeyboardEvent) => onDelete(e);
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [onDelete]);
    return null;
}

// ============================================================================
// Placement interaction (drag from Project Browser / Place View tool)
// ============================================================================

/** Utility: create a placed view entry for a sheet, used by tools. */
export function makePlacedView(viewId: string, position: { x: number; y: number }, scale?: number): PlacedView {
    return {
        id: uuidv4(),
        viewId,
        position,
        viewportType: 'Viewport',
        scale,
    };
}

/** Exported for the Place View tool: available sheet sizes list. */
export function listSheetSizes(): { id: SheetSizeId; label: string; width: number; height: number }[] {
    return Object.entries(SHEET_SIZES).map(([id, meta]) => ({
        id: id as SheetSizeId,
        label: meta.label,
        width: meta.width,
        height: meta.height,
    }));
}
